import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

//import material ui
import {
  Grid,
  Card,
  Paper,
  CardHeader,
  Typography,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Divider,
  Tooltip,
  Pagination,
  TablePagination,
  Fab,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardActions,
  Skeleton,
} from "@mui/material";
import { Box } from "@mui/system";
import { useTheme } from "@mui/material/styles";

import FilterListIcon from "@mui/icons-material/FilterList";
import ArticleIcon from "@mui/icons-material/Article";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";

//project components
import config from "../../../config";
import JobPostForm from "./JobPostForm";
import { setJobPostFilterData } from "../../../store/generalSlice";

//third party
import axios from "axios";
import useAxiosAuth from "../../../hooks/useAxiosAuth";
import { useFormik } from "formik";
import JobPost from "../../../components/job-post/JobPost";
import { convertDateToLocalReverse } from "../../../utils/date-operations";

const JobPosts = () => {
  const navigate = useNavigate();
  const axiosAuth = useAxiosAuth();
  const pageSize = 50;
  const dispatch = useDispatch();
  const theme = useTheme();
  const { userId, userName, role, displayRole } = useSelector(
    (state) => state.userReducer,
  );

  const [jobPosts, setJobPosts] = useState();
  const [selectedJobPost, setSelectedJobPost] = useState();
  const [openJobPostForm, setOpenJobPostForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { jobPostFilterData } = useSelector((state) => state.generalReducer);

  const getJobPosts = (filterData, skip) => {
    let minSalary = filterData.minSalary;
    let maxSalary = filterData.maxSalary;
    if (filterData.minSalary === "") {
      //formik.setFieldValue("minSalary", 0);
      minSalary = 0;
    }
    if (filterData.maxSalary === "") {
      //formik.setFieldValue("maxSalary", 0);
      maxSalary = 0;
    }

    let minExperience = filterData.minExperience;
    let maxExperience = filterData.maxExperience;
    if (
      filterData.minExperience === "" ||
      filterData.minExperience === undefined
    ) {
      //formik.setFieldValue("minSalary", 0);
      minExperience = 0;
    }
    if (
      filterData.maxExperience === "" ||
      filterData.minExperience === undefined
    ) {
      //formik.setFieldValue("maxSalary", 0);
      maxExperience = 0;
    }

    let tempFilterData = {
      ...filterData,
      skip: skip,
      take: pageSize,
      minSalary: minSalary,
      maxSalary: maxSalary,
      minExperience: minExperience,
      maxExperience: maxExperience,
    };
    dispatch(setJobPostFilterData(tempFilterData));
    axiosAuth
      .post("/api/JobPosts/list", tempFilterData)
      .then((response) => {
        if (response.status === 200) {
          console.log(response.data);
          setJobPosts(response.data);
        } else {
          console.log("Error");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const formik = useFormik({
    initialValues: jobPostFilterData,
    onSubmit: (values) => {
      setCurrentPage(1);
      getJobPosts(values, 0);
    },
  });

  useEffect(() => {
    getJobPosts(formik.values, 0);
  }, []);

  return (
    <Grid maxWidth="xl" sx={{ marginX: "auto", paddingY: 4, paddingX: 2 }}>
      <Card elevation={3}>
        <Grid
          sx={{
            background: "linear-gradient(to right,#4a7dcf 0%, #2a569f 100%)",
            paddingX: 2,
            paddingY: 1,
          }}
        >
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5" color="white.main">
              Job Posts
            </Typography>
            <Grid>
              <Tooltip title="Add New">
                <IconButton
                  color="white"
                  sx={{ marginLeft: 2 }}
                  onClick={() => {
                    setSelectedJobPost({
                      id: 0,
                      employerId: 1,
                      jobType: 1,
                      title: "",
                      jobDescription: "",
                      qualification: "",
                      location: "",
                      minSalary: 0,
                      maxSalary: 0,
                      noOfVaccancy: 1,
                      postDate: convertDateToLocalReverse(new Date()),
                      lastDate: convertDateToLocalReverse(new Date()),
                      minExprienceRequired: 0,
                      maxExprienceRequired: 0,
                      comments: "",
                      status: 1,
                    });
                    setOpenJobPostForm(true);
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
        <Grid container justifyContent="center" alignItems="center">
          <Grid
            container
            spacing={3}
            maxWidth="xl"
            sx={{ marginTop: 1, marginBottom: 3, paddingX: 2 }}
          >
            <Grid item lg={4} xl={4} md={4} sm={12} xs={12}>
              <form onSubmit={formik.handleSubmit}>
                <Card variant="outlined">
                  <CardHeader title="Filters"></CardHeader>
                  <Divider />
                  <CardContent>
                    <Stack spacing={2}>
                      <TextField
                        name="specialization"
                        label="Title"
                        fullWidth
                        value={formik.values.specialization}
                        onChange={formik.handleChange}
                      ></TextField>
                      <TextField
                        name="location"
                        label="Location"
                        fullWidth
                        value={formik.values.location}
                        onChange={formik.handleChange}
                      ></TextField>
                      <TextField
                        name="companyName"
                        label="Employer Name"
                        fullWidth
                        value={formik.values.companyName}
                        onChange={formik.handleChange}
                      ></TextField>
                      <FormControl sx={{ marginBottom: 3 }} fullWidth>
                        <InputLabel>Job Type</InputLabel>
                        <Select
                          name="jobType"
                          label="Job Type"
                          onChange={formik.handleChange}
                          value={formik.values.jobType}
                        >
                          <MenuItem value={0}>All</MenuItem>
                          <MenuItem value={1}>Part Time</MenuItem>
                          <MenuItem value={2}>Full Time</MenuItem>
                          <MenuItem value={3}>Contract Basis</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography>Monthly Salary</Typography>
                      <Stack direction="row" spacing={2}>
                        <TextField
                          type="number"
                          name="minSalary"
                          label="Min."
                          fullWidth
                          value={formik.values.minSalary}
                          onChange={(e) =>
                            formik.setFieldValue(
                              "minSalary",
                              e.target.value.replace(/^0+/, ""),
                            )
                          }
                        />
                        <TextField
                          type="number"
                          name="maxSalary"
                          label="Max."
                          fullWidth
                          value={formik.values.maxSalary}
                          onChange={(e) =>
                            formik.setFieldValue(
                              "maxSalary",
                              e.target.value.replace(/^0+/, ""),
                            )
                          }
                        />
                      </Stack>

                      <Typography>Experience Required</Typography>
                      <Stack direction="row" spacing={2}>
                        <TextField
                          type="number"
                          name="minExperience"
                          label="Min."
                          fullWidth
                          value={formik.values.minExperience}
                          onChange={(e) =>
                            formik.setFieldValue(
                              "minExperience",
                              e.target.value.replace(/^0+/, ""),
                            )
                          }
                        />
                        <TextField
                          type="number"
                          name="maxExperience"
                          label="Max."
                          fullWidth
                          value={formik.values.maxExperience}
                          onChange={(e) =>
                            formik.setFieldValue(
                              "maxExperience",
                              e.target.value.replace(/^0+/, ""),
                            )
                          }
                        />
                      </Stack>
                      {role == 1 && (
                        <FormControl sx={{ marginBottom: 3 }} fullWidth>
                          <InputLabel>Job Status</InputLabel>
                          <Select
                            name="status"
                            label="Job Status"
                            onChange={formik.handleChange}
                            value={formik.values.status}
                          >
                            {/* <MenuItem value={0}>All</MenuItem> */}
                            <MenuItem value={1}>Published</MenuItem>
                            <MenuItem value={2}>Unpublished</MenuItem>
                          </Select>
                        </FormControl>
                      )}

                      {role == 1 && (
                        <FormControl sx={{ marginBottom: 3 }} fullWidth>
                          <InputLabel>Internal Status</InputLabel>
                          <Select
                            name="otherStatus"
                            label="Other Status"
                            onChange={formik.handleChange}
                            value={formik.values.otherStatus}
                          >
                            <MenuItem value={0}>All</MenuItem>
                            <MenuItem value={1}>Searching</MenuItem>
                            <MenuItem value={2}>Refine Search</MenuItem>
                            <MenuItem value={3}>Profile Sent</MenuItem>
                            <MenuItem value={4}>Int. Arranged</MenuItem>
                            <MenuItem value={5}>Int Result ?</MenuItem>
                            <MenuItem value={6}>Offer Sent</MenuItem>
                            <MenuItem value={7}>Joined</MenuItem>
                            <MenuItem value={8}>Not Joining</MenuItem>
                            <MenuItem value={9}>Closed by Co.</MenuItem>
                            <MenuItem value={10}>Job Left</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    </Stack>
                  </CardContent>
                  <Divider />
                  <CardActions sx={{ paddingY: 3, justifyContent: "center" }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        //dispatch(setJobPostFilterData(formik.values));
                        formik.handleSubmit();
                      }}
                    >
                      Apply
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        let tempFilterData = {
                          companyName: "",
                          jobType: 0,
                          position: "",
                          location: "",
                          specialization: "",
                          minSalary: 0,
                          maxSalary: 0,
                          minExperience: 0,
                          maxExperience: 0,
                          status: 1,
                          skip: 0,
                          take: pageSize,
                        };
                        formik.setValues(tempFilterData);
                        setCurrentPage(1);
                        getJobPosts(tempFilterData, 0);
                      }}
                    >
                      Clear
                    </Button>
                  </CardActions>
                </Card>
              </form>
            </Grid>
            <Grid item lg={8} xl={8} md={8} sm={12} xs={12}>
              <Card
                variant="outlined"
                sx={{
                  padding: 2,
                  marginBottom: 2,
                  display: "flex",
                  direction: "row",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                {jobPosts != undefined ? (
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography>Total: {jobPosts.count}</Typography>
                    <Pagination
                      count={Math.ceil(jobPosts.count / pageSize)}
                      page={currentPage}
                      variant="outlined"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                      color="primary"
                      onChange={(event, newPage) => {
                        setCurrentPage(newPage);
                        getJobPosts(
                          jobPostFilterData,
                          (newPage - 1) * pageSize,
                        );
                      }}
                    />
                  </Stack>
                ) : null}
              </Card>
              {/* {jobPosts?.jobPosts.map((row) => (
                <Box key={row.id} sx={{ marginBottom: 2 }}>
                  <JobPost jobPost={row} type={2} />
                </Box>
              ))} */}

              {jobPosts != undefined ? (
                <>
                  {jobPosts.jobPosts.length > 0 ? (
                    <>
                      {jobPosts.jobPosts.map((row) => (
                        <Box key={row.id} sx={{ marginBottom: 2 }}>
                          <JobPost jobPost={row} type={2} />
                        </Box>
                      ))}
                    </>
                  ) : (
                    <Card
                      variant="outlined"
                      sx={{
                        padding: 4,
                        marginBottom: 2,
                        backgroundColor: theme.palette.grey[200],
                      }}
                    >
                      <Typography textAlign="center">
                        Oops! There are no job posts for your filter criteria.
                      </Typography>
                    </Card>
                  )}
                </>
              ) : (
                <Stack direction="column" spacing={2}>
                  <Skeleton variant="rectangular" fullWidth height={70} />
                  <Skeleton variant="rectangular" fullWidth height={70} />
                  <Skeleton variant="rectangular" fullWidth height={70} />
                </Stack>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Card>

      <Dialog
        fullWidth
        maxWidth="xl"
        open={openJobPostForm}
        onClose={() => {
          setOpenJobPostForm(false);
        }}
      >
        <DialogContent>
          <JobPostForm
            jobPost={selectedJobPost}
            onSave={() => {
              setCurrentPage(1);
              getJobPosts(formik.values, 0);
              setOpenJobPostForm(false);
            }}
            onCancel={() => {
              setOpenJobPostForm(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default JobPosts;
